import { Badge, PageHeader, Skeleton } from '@/components';
import { useReleaseIntegrations, useReleaseProducts } from '@/features/release-config/hooks';
import { A } from '@solidjs/router';
import { RELEASE_ADAPTER_LABELS } from '@uchat-team-management/shared';
import { For, Show } from 'solid-js';

const IntegrationBadge = (props: { label: string; connected: boolean | undefined }) => (
  <div class="flex items-center gap-2">
    <span class="w-20 text-sm text-neutral-600 dark:text-neutral-300">{props.label}</span>
    <Badge tone={props.connected ? 'success' : 'neutral'}>
      {props.connected ? 'Connected' : 'Not connected'}
    </Badge>
  </div>
);

export default function ReleaseProductsIndex() {
  const products = useReleaseProducts(true);
  const integrations = useReleaseIntegrations();

  return (
    <section>
      <PageHeader
        title="Products"
        sub="Per-product release configuration and integration status."
      />

      <div class="mx-auto max-w-6xl space-y-6 p-6">
        <div class="rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
          <h2 class="mb-3 font-medium">Integrations</h2>
          <div class="flex flex-col gap-2">
            <IntegrationBadge label="GitHub" connected={integrations.data?.data.github} />
            <IntegrationBadge label="ClickUp" connected={integrations.data?.data.clickup} />
            <IntegrationBadge label="Linear" connected={integrations.data?.data.linear} />
          </div>
          <p class="mt-3 text-xs text-neutral-500">
            Credentials are configured server-side via environment variables and are never shown
            here.
          </p>
        </div>

        <Show when={!products.isPending} fallback={<Skeleton class="h-40 w-full" />}>
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <For each={products.data?.data ?? []}>
              {(product) => (
                <A
                  href={`/release/products/${product.id}`}
                  class="rounded-xl border border-neutral-200 bg-surface p-4 transition-shadow duration-200 ease-out hover:shadow-md motion-reduce:transition-none dark:border-neutral-800 dark:bg-surface-dark"
                >
                  <div class="flex items-center justify-between">
                    <div>
                      <h3 class="font-medium">{product.name}</h3>
                      <p class="text-xs text-neutral-500">{product.slug}</p>
                    </div>
                    <div class="flex items-center gap-2">
                      <Show when={product.disabledAt}>
                        <Badge tone="danger">Disabled</Badge>
                      </Show>
                      <Badge tone="neutral">{RELEASE_ADAPTER_LABELS[product.adapter]}</Badge>
                    </div>
                  </div>
                  <p class="mt-2 text-xs text-neutral-500">
                    {product.repoOwner && product.repoName
                      ? `${product.repoOwner}/${product.repoName}`
                      : 'No repository configured'}
                  </p>
                </A>
              )}
            </For>
          </div>
        </Show>
      </div>
    </section>
  );
}
